import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, FileText, ImageIcon, Printer, X } from 'lucide-react'
import { BomTables } from '@/components/files/BomTables'
import { Button } from '@/components/ui/Button'

const LazyPdfViewer = lazy(() =>
  import('@/components/files/PdfViewer').then((m) => ({ default: m.PdfViewer })),
)
import { parseBomFromDataUrl, type BomDocument } from '@/lib/bom'
import { isBomName, isImageName, isPdfName } from '@/lib/files'
import type { WorkspaceNode } from '@/services/workspace'

type FileReaderOverlayProps = {
  file: WorkspaceNode
  images: WorkspaceNode[]
  onClose: () => void
  onShowImage: (file: WorkspaceNode) => void
  onDownload: (file: WorkspaceNode) => void
}

export function FileReaderOverlay({
  file,
  images,
  onClose,
  onShowImage,
  onDownload,
}: FileReaderOverlayProps) {
  const [broken, setBroken] = useState(false)
  const [bom, setBom] = useState<BomDocument | null>(null)
  const [bomError, setBomError] = useState<string | null>(null)
  const [bomLoading, setBomLoading] = useState(false)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    setBroken(false)
    setBom(null)
    setBomError(null)
  }, [file.id])

  useEffect(() => {
    if (!isBomName(file.name)) return
    if (!file.dataUrl) {
      setBomError('Không có nội dung định mức để đọc. Hãy tải file Excel lên.')
      return
    }
    let cancelled = false
    setBomLoading(true)
    parseBomFromDataUrl(file.dataUrl, file.name)
      .then((doc) => {
        if (cancelled) return
        if (!doc.wood.length && !doc.hardwareTables.length && !doc.hardware.length) {
          setBomError('Không tìm thấy bảng gỗ hoặc ngũ kim trong file.')
          return
        }
        setBom(doc)
      })
      .catch((err) => {
        if (!cancelled) {
          setBomError(err instanceof Error ? err.message : 'Không đọc được file định mức.')
        }
      })
      .finally(() => {
        if (!cancelled) setBomLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [file])

  const imageIndex = useMemo(() => {
    if (!isImageName(file.name)) return -1
    return images.findIndex((item) => item.id === file.id)
  }, [file, images])

  function go(delta: number) {
    if (imageIndex < 0 || images.length === 0) return
    const next = images[(imageIndex + delta + images.length) % images.length]
    onShowImage(next)
  }

  if (isPdfName(file.name)) {
    if (!file.dataUrl) {
      return (
        <div className="file-reader-overlay flex h-full min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-2 bg-slate-50 p-6 text-center">
          <FileText className="h-8 w-8 text-slate-400" />
          <p className="text-sm font-medium text-slate-800">{file.name}</p>
          <p className="text-xs text-slate-500">
            File PDF này chưa có nội dung. Hãy tải PDF lên lại để xem trong trang.
          </p>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Đóng
          </Button>
        </div>
      )
    }
    return (
      <Suspense
        fallback={
          <div className="flex h-full items-center justify-center text-sm text-slate-500">
            Đang tải tài liệu PDF...
          </div>
        }
      >
        <LazyPdfViewer
          fileName={file.name}
          dataUrl={file.dataUrl}
          onClose={onClose}
          onDownload={() => onDownload(file)}
        />
      </Suspense>
    )
  }

  return (
    <div className="file-reader-overlay flex h-full min-h-0 min-w-0 flex-1 flex-col bg-slate-100">
      <div className="file-reader-print-root flex min-h-0 flex-1 flex-col bg-white">
        <div className="no-print flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-3 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">{file.name}</p>
            <p className="text-xs text-slate-400">
              Chỉ đọc
              {isBomName(file.name)
                ? bom?.hardwareTables?.length
                  ? ` · gỗ + ${bom.hardwareTables.length} bảng ngũ kim`
                  : ' · gỗ + ngũ kim'
                : ''}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {isBomName(file.name) ? (
              <Button size="sm" variant="secondary" onClick={() => window.print()}>
                <Printer className="h-3.5 w-3.5" />
                In
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="secondary"
              disabled={!file.dataUrl}
              onClick={() => onDownload(file)}
            >
              <Download className="h-3.5 w-3.5" />
              Tải xuống
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose} aria-label="Đóng">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="file-reader-body min-h-0 flex-1 overflow-auto bg-slate-200/70">
          {isImageName(file.name) && file.dataUrl && !broken ? (
            <div className="relative flex min-h-full items-center justify-center p-6">
              <img
                src={file.dataUrl}
                alt={file.name}
                className="max-h-full max-w-full object-contain shadow"
                onError={() => setBroken(true)}
              />
              {images.length > 1 ? (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute top-1/2 left-3 -translate-y-1/2"
                    onClick={() => go(-1)}
                    aria-label="Ảnh trước"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute top-1/2 right-3 -translate-y-1/2"
                    onClick={() => go(1)}
                    aria-label="Ảnh sau"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </>
              ) : null}
            </div>
          ) : isBomName(file.name) ? (
            bomLoading ? (
              <p className="p-6 text-sm text-slate-500">Đang đọc file định mức...</p>
            ) : bomError ? (
              <EmptyRead message={bomError} name={file.name} />
            ) : bom ? (
              <BomTables doc={bom} />
            ) : (
              <EmptyRead message="Không đọc được file định mức." name={file.name} />
            )
          ) : (
            <EmptyRead
              name={file.name}
              message={
                isPdfName(file.name) || isImageName(file.name)
                  ? 'Không có nội dung để xem. Hãy tải file lên.'
                  : 'Định dạng này chưa hỗ trợ xem trực tiếp. Bạn vẫn có thể tải file về.'
              }
            />
          )}
        </div>
      </div>
    </div>
  )
}

function EmptyRead({ name, message }: { name: string; message: string }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-2 px-6 text-center text-slate-500">
      {isImageName(name) ? (
        <ImageIcon className="h-8 w-8 text-slate-400" />
      ) : (
        <FileText className="h-8 w-8 text-slate-400" />
      )}
      <p className="text-sm font-medium text-slate-800">{name}</p>
      <p className="text-xs">{message}</p>
    </div>
  )
}
